import axios from "axios";
import { formatDate, isValidUUID } from "./globalsUtils";
import { EndUser } from "models/profile/EndUser";
import { AssetStatus, Profile, ProfileAsset } from "models/profile/Profile";
import { useMaterialUIController } from "context";
import { makeRequest, networkGlobals } from "./networkUtils";
import { mmAction } from "./matchmakerUtils";
import { ProfileQResponse } from "models/profile/ProfileResponse";
import { MatchScore } from "models/introduction/matchScore";

export const getByEmailPhone = async (
  opt: { search: string }
): Promise<Profile[]> => {
  let searchBY,
    search = opt.search;
  let phone = search
    .replace(" ", "")
    .replace("-", "")
    .replace("(", "")
    .replace(")", "")
    .replace("+1", "")
    .replace("+", "");

    const firstToken = search.split(/,|;|\s/)[0]

  if (/^\d{3}$/.test(search)) {
    search = "+15555555" + search;
    searchBY = "phone_number";
  } else if (/^\+?\d+$/.test(phone)) {
    search = `+1${phone}`;
    searchBY = "phone_number";
  } else if (/^.+@.+\..+$/.test(opt.search)) {
    searchBY = "email";
  } else if (isValidUUID(firstToken)) {
    searchBY = "id";
  } else {
    searchBY = "name"
  }
  return makeRequest(
    `profiles/cognito?searchBY=${searchBY}&userID=${encodeURIComponent(search)}`
  );
};

export const getPendingProfiles = async (brandID: string) => {
  return makeRequest(`profiles/adminList?brandID=${brandID || ""}&status=6`);
};

export const getProfileStats = async () => {
  return makeRequest(`profiles/stats`);
};

let profilesCache: { [key: string]: Profile[] } = {};
export const getEndusersList = async (
  status: string[],
  showInternal: boolean,
  page: number
) : Promise<Profile[]> => {
  const key = `${networkGlobals.brand.id}-${page}-${status.join(",")} - ${showInternal}`;
  if (!profilesCache[key]) {
    profilesCache[key] = await makeRequest(
      `profiles?page=${page}&status=${status.join(",")}&showInternal=${showInternal}`
    );
  }
  return profilesCache[key];
};

export const getByUserId = async (
  userID: string
): Promise<{ user: EndUser; profile: Profile; profileAssets: ProfileAsset[] }> => {
  return makeRequest(`profiles/${userID}`);
};

export const getQuestionnaireStats = async (profileId: string) => {
  return makeRequest(`profiles/${profileId}/questionnaires-stats`);
};

export const getStatistics = async (profileId: string) => {
  return makeRequest(`profiles/${profileId}/statistics`);
};

export const getIntrosByFilter = async (profileId: string, filter: string) => {
  return makeRequest(`profiles/${profileId}/introductions?filter=${filter}`);
};

export const getImages = async (profileId: string) => {
  return makeRequest(`profiles/${profileId}/images`);
};

export const getUsageStats = async (profileId: string) => {
  return makeRequest(`profiles/${profileId}/usage-statistics`);
};

export const getTickets = async (profileId: string) => {
  return makeRequest(`profiles/${profileId}/tickets`);
};

export const updateStatus = async (profileId: string, statusId: number, statusReason: string) => {
  const res = makeRequest(`profiles/${profileId}/status`, {
    method: "PUT",
    body: { statusId, statusReason },
  }).then(() => (profilesCache = {}));
  await mmAction("", "approveEndUser", "", "", profileId);
  return res;
};

export const updateEndUser = async (profileId: string, data: any) => {
  const res = await makeRequest(`profiles/${profileId}/cognito`, { method: "PUT", body: data }).then(
    (r) => {
      profilesCache = {};
      return r;
    }
  );
  await mmAction("", "approveEndUser", "", "", profileId);
  return res;
};

export const updateProfileAttr = async (profileId: string, data: any) => {
  return await makeRequest(`profiles/${profileId}/attributes`, {
    method: "PUT",
    body: data,
  })
}

export const updateProfile = async (profileId: string, data: any) => {
  return await makeRequest(`profiles/${profileId}`, {
    method: "PUT",
    body: data,
  })
}

export const getNotifcationsSettings = async (profileId: string) => {
  return makeRequest(`profiles/${profileId}/notifications`);
};

export const updateNotificationSetting = async (
  profileId: string,
  channel_id: number,
  category_id: number,
  state: boolean
) => {
  return makeRequest(`profiles/${profileId}/notifications`, {
    method: "PUT",
    body: { channel_id, category_id, state },
  });
};

export const updateImageStatus = async (profileId: string, imageId: string, status) => {
  return makeRequest(`profiles/${profileId}/images/${imageId}`, {
    method: "PUT",
    body: { status },
  });
};

export const deleteProfile = async (profileId: string) => {
  return makeRequest(`profiles/${profileId}`, { method: "DELETE" }).then(
    () => (profilesCache = {})
  );
};

export const getProfileResponses = async (profileId: string,ids:string[] = []): Promise<ProfileQResponse[]> => {
  return makeRequest(`profiles/${profileId}/responses?ids=${ids.join(",")}`);
};

export const getProfilePartnersResponses = async (profileId: string, ids:string[] = []): Promise<ProfileQResponse[]> => {
  return makeRequest(`profiles/${profileId}/partners-responses?ids=${ids.join(",")}`);
}

export const getProfileMatchScores = async (
  profileId: string,
  filter,
  page
): Promise<MatchScore[]> => {
  return makeRequest(
    `profiles/${profileId}/match-scores?page=${page}&filters=${JSON.stringify(filter)}`
  );
};

export const getProfileByAttribute = async (attrKey: string, attrValue: string): Promise<Profile[]> => {
  return makeRequest(`profiles/byAttribute/${attrKey}?attrValue=${attrValue}`);
};

export const cachedNames:EndUser[] = [];
export const getNamesAsync = async (userIds: string[], profileIds: { [key: string]: string } = {}) => {

  const cached = cachedNames.filter((c) => userIds.includes(c.id!));
  if (cached.length) {
    document.dispatchEvent(new CustomEvent("userNames", { detail: cached }));
  }
  userIds = userIds.filter((p) => !cached.find((c) => c.id === p));
  
  for (let i = 0; i < userIds.length; i += 10) {
    const ids = userIds.slice(i, i + 10);
    const res = await makeRequest(`profiles/names?ids=${ids.join(",")}`, {withoutError: true}) as EndUser[];
    if (!res) return
    res.forEach(r => {
      r.profileId = profileIds[r.id!];
    })
    cachedNames.push(...res);
    document.dispatchEvent(new CustomEvent("userNames", { detail: cachedNames }));
  }
}

export const getNamesByProfileIds = async (profileIds: string[]) => {
  const profiles = await getByEmailPhone({search: profileIds.join(",")})
  return getNamesAsync(profiles.map(p => p.userId!), profiles.reduce((acc, p) => {
    acc[p.userId!] = p.profileId;
    return acc;
  }, {}))
}

export const getEmailsList = (userIds: string[]): string[] => {
  return cachedNames.filter((c) => userIds.includes(c.id!)).map((c) => c.email).filter((e) => e) as string[];
}

export const getProfilesWithoutIntro = async (page:number)  : Promise<Profile[]> => {
  return makeRequest(`profiles/filters/noIntroductions?page=${page}`);
}

export const getProfileStatusLogs = async (profileId: string) => {
  return makeRequest(`profiles/${profileId}/statusLogs`);
};

export const getAllProfileAssets = async (pending: boolean, page: number): Promise<ProfileAsset[]> => {
  return makeRequest(`profiles/images?page=${page}&pending=${pending}`).then((res) => res.map((asset) => Object.assign(new ProfileAsset(), asset)));
};

export const getProfileExternalInfo = async (profileId: string) => {
  return makeRequest(`profiles/${profileId}/external-info`);
};